import React, { useMemo, useState } from 'react';
import NovaAvatarImg from '../components/NovaAvatarImg';
import { NovaUnifiedPageBar } from '../components/NovaUnified';
import {
  demoChapterStackColumnStyle,
  demoSectionCardStackStyle,
  DEMO_MINI_CARD_GRID_GAP_PX,
  DEMO_SECTION_STACK_GAP_PX,
  FERTILITY_DEMO_SECTION_BORDER,
} from '../designSystem';
import {
  fertilityWhenToSeeDoctorDayData,
  signKindFromText,
} from '../data/fertilityWhenToSeeDoctorDayData';

/** Demo context — used by tests / future wiring */
export const patientContext = {
  cyclePhase: 'stims',
  stimDay: 8,
  protocol: 'antagonist',
  afc: 12,
  cycleNumber: 2,
  estDaysToRetrieval: 9,
};

const FONT = "'Plus Jakarta Sans', -apple-system, sans-serif";

const clabel = {
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  color: 'var(--muted)',
  marginBottom: 8,
};

const mainCardStyle = {
  background: 'var(--white)',
  border: FERTILITY_DEMO_SECTION_BORDER,
  borderRadius: 14,
  padding: '16px 18px',
};

const infoTile = {
  background: 'var(--sand, #f9f6f1)',
  borderRadius: 10,
  padding: '12px 14px',
};

/** Matches past stim-day dots: sage wash + green border */
const easeTipsBox = {
  background: 'var(--sage-l, #e1f5ee)',
  border: '0.5px solid rgba(29, 158, 117, 0.35)',
  borderRadius: 10,
  padding: '12px 14px',
  marginTop: 10,
};

function bubbleBg(variant) {
  if (variant === 'trigger') return 'rgba(212, 83, 126, 0.1)';
  if (variant === 'recovery') return 'var(--sage-l, #e1f5ee)';
  return 'var(--pink-l, #fbeaf0)';
}

function ohssBoxStyle(level) {
  if (level === 'low') {
    return {
      background: 'var(--sage-l, #e1f5ee)',
      border: '0.5px solid rgba(29, 158, 117, 0.35)',
    };
  }
  if (level === 'elevated') {
    return {
      background: 'var(--amber-l, #fef0eb)',
      border: '0.5px solid rgba(173, 63, 26, 0.35)',
    };
  }
  return {
    background: 'var(--amber-l, #fef0eb)',
    border: '0.5px solid rgba(173, 63, 26, 0.25)',
  };
}

function ArrowBtn({ onClick, disabled, children }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      style={{
        width: 28,
        height: 28,
        borderRadius: 8,
        border: '0.5px solid var(--pink-m, #f4c0d1)',
        background: disabled ? 'var(--sand-2)' : 'var(--pink-l)',
        color: disabled ? 'var(--muted)' : 'var(--pink-d)',
        fontSize: 12,
        fontWeight: 700,
        cursor: disabled ? 'default' : 'pointer',
        fontFamily: FONT,
        lineHeight: 1,
        padding: 0,
      }}
    >
      {children}
    </button>
  );
}

/** @type {import('react').FC<{ navigate: (path: string) => void }>} */
export default function FertilityDemoWhenToSeeDoctor({ navigate }) {
  const [currentDay, setCurrentDay] = useState(8);

  const data = fertilityWhenToSeeDoctorDayData[currentDay];
  const demoToday = patientContext.stimDay;

  const badge = useMemo(() => {
    if (currentDay < demoToday) {
      return { label: 'Past', bg: 'var(--sage-l, #e1f5ee)', color: '#085041' };
    }
    if (currentDay === demoToday) {
      return { label: 'Today', bg: 'var(--pink-l, #fbeaf0)', color: 'var(--pink-d, #993556)' };
    }
    return { label: 'Coming up', bg: 'var(--sand-2, #ede8df)', color: 'var(--muted)' };
  }, [currentDay, demoToday]);

  const inc = () => setCurrentDay((d) => Math.min(14, d + 1));
  const dec = () => setCurrentDay((d) => Math.max(1, d - 1));

  return (
    <div
      style={{
        ...demoChapterStackColumnStyle,
        padding: `0 0 ${DEMO_SECTION_STACK_GAP_PX}px`,
        fontFamily: FONT,
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      <NovaUnifiedPageBar
        layout="stacked"
        contextPill="Today for you"
        body={data.novaGuidance}
        actionLabel="Ask Nova →"
        onAction={() => navigate('/nova')}
      />

      {/* Main card */}
      <div style={mainCardStyle}>
        {/* A) Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
          <div style={{ flex: '1 1 200px', minWidth: 0 }}>
            <h2
              style={{
                fontFamily: 'Georgia, "Times New Roman", serif',
                fontSize: 16,
                fontWeight: 700,
                color: 'var(--text)',
                margin: '0 0 4px 0',
              }}
            >
              Stim day {currentDay}
            </h2>
            <p style={{ fontSize: 12, color: 'var(--muted)', margin: 0, lineHeight: 1.45 }}>{data.headerSubtitle}</p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8, flexShrink: 0 }}>
            <span
              style={{
                fontSize: 10,
                fontWeight: 700,
                padding: '3px 10px',
                borderRadius: 10,
                background: badge.bg,
                color: badge.color,
              }}
            >
              {badge.label}
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 10, color: 'var(--muted)' }}>Stim day</span>
              <ArrowBtn disabled={currentDay <= 1} onClick={dec}>
                ◀
              </ArrowBtn>
              <span
                style={{
                  minWidth: 36,
                  textAlign: 'center',
                  fontSize: 13,
                  fontWeight: 700,
                  background: 'var(--pink, #d4537e)',
                  color: 'white',
                  padding: '5px 12px',
                  borderRadius: 999,
                }}
              >
                {currentDay}
              </span>
              <ArrowBtn disabled={currentDay >= 14} onClick={inc}>
                ▶
              </ArrowBtn>
            </div>
          </div>
        </div>

        {/* B) Day dots */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ ...clabel, marginBottom: 6 }}>All stim days — tap to explore</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {Array.from({ length: 14 }, (_, i) => {
              const d = i + 1;
              const isActive = d === currentDay;
              const isTodayMarker = d === demoToday && !isActive;
              const isPast = d < currentDay;
              const isFuture = d > currentDay;

              let bg;
              let border;
              let color;
              let fontWeight = 700;
              if (isActive) {
                bg = 'var(--pink, #d4537e)';
                border = '2px solid var(--pink, #d4537e)';
                color = 'white';
              } else if (isTodayMarker) {
                bg = 'white';
                border = '2px solid var(--pink-d, #993556)';
                color = 'var(--pink-d, #993556)';
              } else if (isPast) {
                bg = 'var(--sage-l, #e1f5ee)';
                border = '2px solid var(--sage, #1d9e75)';
                color = '#085041';
              } else {
                bg = 'var(--white, #fffcf9)';
                border = '2px solid var(--sand-2, #ede8df)';
                color = 'var(--muted)';
                fontWeight = 600;
              }

              return (
                <button
                  key={d}
                  type="button"
                  onClick={() => setCurrentDay(d)}
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    border,
                    background: bg,
                    color,
                    fontSize: 11,
                    fontWeight,
                    cursor: 'pointer',
                    fontFamily: FONT,
                    padding: 0,
                    flexShrink: 0,
                  }}
                  aria-label={`Stim day ${d}`}
                >
                  {d}
                </button>
              );
            })}
          </div>
        </div>

        {/* C) Day narrative bubble */}
        <div
          style={{
            borderRadius: 12,
            padding: '12px 14px',
            marginBottom: 14,
            background: bubbleBg(data.bubbleVariant),
            border: '0.5px solid var(--pink-m, #f4c0d1)',
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--pink-d, #993556)', marginBottom: 6 }}>{data.phaseLabel}</div>
          <div style={{ fontSize: 12, color: '#2d1a22', lineHeight: 1.55 }}>{data.phaseSummary}</div>
        </div>

        {/* Two-column grid */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            gap: 14,
            alignItems: 'start',
          }}
        >
          <div style={demoSectionCardStackStyle}>
            <div>
            <div style={clabel}>What your body is doing</div>
            <div style={infoTile}>
              {data.bodyDoing.map((line) => (
                <div key={line} style={{ fontSize: 11, color: '#2d1a22', lineHeight: 1.55, padding: '4px 0', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                  <span style={{ color: 'var(--sage, #1d9e75)', fontWeight: 700, flexShrink: 0, lineHeight: 1.55 }} aria-hidden>
                    ✓
                  </span>
                  <span style={{ color: '#2d1a22' }}>{line}</span>
                </div>
              ))}
            </div>
            </div>
            <div>
            <div style={clabel}>What&apos;s normal to feel</div>
            <div style={infoTile}>
              {data.normalToFeel.map((line) => (
                <div key={line} style={{ fontSize: 11, lineHeight: 1.55, padding: '4px 0', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                  <span style={{ color: 'var(--sage, #1d9e75)', fontWeight: 700, flexShrink: 0, lineHeight: 1.55 }} aria-hidden>
                    ✓
                  </span>
                  <span style={{ color: '#2d1a22' }}>{line}</span>
                </div>
              ))}
            </div>
            <div style={easeTipsBox}>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: '#085041',
                  marginBottom: 8,
                }}
              >
                Small things that help today
              </div>
              {data.easeTips.map((line) => (
                <div
                  key={line}
                  style={{
                    fontSize: 11,
                    lineHeight: 1.55,
                    padding: '4px 0',
                    display: 'flex',
                    gap: 8,
                    alignItems: 'flex-start',
                    color: '#2d1a22',
                  }}
                >
                  <span style={{ color: 'var(--sage, #1d9e75)', fontWeight: 700, flexShrink: 0, lineHeight: 1.55 }} aria-hidden>
                    •
                  </span>
                  <span>{line}</span>
                </div>
              ))}
            </div>
            </div>
          </div>

          <div style={demoSectionCardStackStyle}>
            <div>
            <div style={clabel}>Typical numbers at day {currentDay}</div>
            <div style={infoTile}>
              {data.numbers.map((row) => (
                <div
                  key={row.label}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 10,
                    fontSize: 11,
                    padding: '6px 0',
                    borderBottom: '0.5px solid var(--sand-2, #ede8df)',
                  }}
                >
                  <span style={{ color: 'var(--muted)' }}>{row.label}</span>
                  <span style={{ fontWeight: 600, color: 'var(--text)', textAlign: 'right' }}>{row.value}</span>
                </div>
              ))}
            </div>
            </div>

            <div>
            <div style={clabel}>OHSS context</div>
            <div style={{ ...infoTile, ...ohssBoxStyle(data.ohssLevel) }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: data.ohssLevel === 'watch' || data.ohssLevel === 'elevated' ? 'var(--amber, #ad3f1a)' : '#085041', marginBottom: 6 }}>
                {data.ohssContext.title}
              </div>
              <div style={{ fontSize: 11, color: '#2d1a22', lineHeight: 1.5 }}>{data.ohssContext.body}</div>
            </div>
            </div>

            <div>
            <div style={clabel}>Signs worth noting today</div>
            <div style={infoTile}>
              {data.signsWorthNoting.map((text) => {
                const kind = signKindFromText(text);
                return (
                  <div key={text} style={{ fontSize: 11, lineHeight: 1.55, padding: '4px 0', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                    <span style={{ flexShrink: 0, lineHeight: 1.55 }} aria-hidden>
                      {kind === 'ok' && <span style={{ color: 'var(--sage, #1d9e75)', fontWeight: 700 }}>✓</span>}
                      {kind === 'call' && <span style={{ fontSize: 11 }}>📞</span>}
                      {kind === 'er' && <span style={{ fontSize: 11, fontWeight: 700, color: '#c0392b' }}>🚨</span>}
                    </span>
                    <span style={{ color: '#2d1a22' }}>{text}</span>
                  </div>
                );
              })}
            </div>
            </div>
          </div>
        </div>

        {/* Reading for today */}
        <div
          style={{
            borderTop: '0.5px solid rgba(212, 83, 126, 0.15)',
            marginTop: 16,
            paddingTop: 14,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
            <NovaAvatarImg size={28} chapter="fertility" alt="Nova" />
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--pink-d, #993556)' }}>Reading for today</div>
              <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 2 }}>
                {`Nova picked these for stim day ${currentDay} · updates each day`}
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: DEMO_MINI_CARD_GRID_GAP_PX }}>
            {data.articles.map((a, idx) => (
              <button
                key={`${a.url}-${idx}`}
                type="button"
                onClick={() => window.open(a.url, '_blank', 'noopener,noreferrer')}
                style={{
                  background: 'var(--pink-l, #fbeaf0)',
                  border: '0.5px solid var(--pink-m, #f4c0d1)',
                  borderRadius: 10,
                  padding: '10px 12px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4,
                  cursor: 'pointer',
                  textAlign: 'left',
                  fontFamily: FONT,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(212, 83, 126, 0.15)';
                  e.currentTarget.style.borderColor = 'var(--pink, #d4537e)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'var(--pink-l, #fbeaf0)';
                  e.currentTarget.style.borderColor = 'var(--pink-m, #f4c0d1)';
                }}
              >
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: 'var(--pink-d, #993556)',
                    background: 'rgba(212, 83, 126, 0.12)',
                    borderRadius: 4,
                    padding: '2px 6px',
                    alignSelf: 'flex-start',
                  }}
                >
                  {a.source}
                </span>
                <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--pink-d, #993556)', lineHeight: 1.4, flex: 1 }}>{a.title}</span>
                <span style={{ fontSize: 10, fontStyle: 'italic', color: 'var(--muted)' }}>{a.author}</span>
                <span style={{ fontSize: 10, color: 'var(--muted)' }}>
                  {a.min} min read →
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
